import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';
import api from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] } })
};

export default function InstrumentsPage() {
  usePageTitle('Instruments');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get('/products').then(res => setProducts(res.data)).catch(console.error);
  }, []);

  const instruments = [
    ...products.map(p => ({
      label: p.prelaunch ? 'Pre-Launch' : 'Available',
      title: p.name,
      tagline: p.tagline,
      desc: p.description,
      price: p.prelaunch ? 'Join Waitlist' : `$${p.price?.toFixed(2)}`,
      href: `/oracle-deck`,
      cta: p.prelaunch ? 'Join the Waitlist' : 'View Product',
      internal: true,
      featured: true
    })),
    {
      label: 'Digital Tool',
      title: 'Prism Planner',
      tagline: 'Drag. Prioritize. Execute.',
      desc: 'A personal productivity tool with a drag-and-drop priority matrix. Capture tasks, sort them by impact and effort, and get an auto-generated to-do list based on your energy level.',
      price: '$49',
      href: '/planner',
      cta: 'Get Lifetime Access',
      internal: true,
      featured: false
    }
  ];

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero */}
      <section className="hero-gradient relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1608549785765-036974ba067d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
            <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--silver))] mb-6">
              Products & Tools
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.08] text-[hsl(var(--foreground))] mb-6">
              Instruments
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">
              Tangible tools and products designed to bring the methodology into your hands. Pattern-recognition instruments for the conscious builder.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Instruments */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-8">
          {instruments.map((item, i) => (
            <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
              <div className={`rounded-xl border p-6 sm:p-8 transition-all duration-300 ${
                item.featured
                  ? 'bg-[hsl(var(--card))] border-[rgba(201,168,76,0.25)] shadow-[0_18px_60px_rgba(0,0,0,0.35)] hover:border-[rgba(201,168,76,0.4)]'
                  : 'bg-[hsl(var(--card))] border-[rgba(168,181,178,0.12)] hover:border-[rgba(201,168,76,0.2)]'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <p className={`font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] ${
                    item.featured ? 'text-[hsl(var(--gold))]' : 'text-[hsl(var(--silver))]'
                  }`}>
                    {item.label}
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-xl font-bold text-[hsl(var(--gold))]">{item.price}</p>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-2 leading-[1.15]">
                  {item.title}
                </h3>
                {item.tagline && (
                  <p className="font-['Cormorant_Garamond'] text-lg italic text-[hsl(var(--champagne))] mb-4">{item.tagline}</p>
                )}
                <p className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))] mb-6">{item.desc}</p>
                {item.internal ? (
                  <Link to={item.href} className="inline-flex items-center gap-2 font-['Inter'] text-sm font-medium text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                    {item.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-['Inter'] text-sm font-medium text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                    {item.cta} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
